import { UTCDate } from '@date-fns/utc';
import { TransactionStatus, TransactionType } from '@seapoint/server/enums';
import { ColumnDef } from '@tanstack/react-table';
import { format, getTime, isSameYear } from 'date-fns';
import { ChevronRight, Clock3, XCircle } from 'lucide-react';
import { AmountDisplay } from '../../../../design-system/amount-display/amount-display';
import { AvatarNameDescription } from '../../../../design-system/avatar/avatar-name-description/avatar-name-description';
import { ColumnHeaderWithSorting } from '../../../../design-system/data-table/column-header-with-sorting';
import { Inline } from '../../../../design-system/inline/inline';
import { Stack } from '../../../../design-system/stack/stack';
import { TransactionView } from '../../../../types/entity-types';
import { exhaustiveCheck } from '../../../../utils/exhaustive-check';
import { formatCurrency } from '../../../../utils/format-currency';
import { getNormalizedAmount } from '../../../../utils/get-normalized-amount';

const ICON_SIZE = 16;
const ICON_COLOR = '#86868B';

export const transactionsTableColumnsDefinition: ColumnDef<TransactionView>[] = [
  {
    id: 'fspDate',
    // Gets the date in milliseconds so we can filter by date range
    accessorFn: (row) => getTime(row.fspDate),
    header: ({ column }) => <ColumnHeaderWithSorting column={column} label="Date" />,
    cell: ({ row }) => {
      const transactionDate = row.original.fspDate;
      const currentYear = new UTCDate();
      const dateFormat = isSameYear(transactionDate, currentYear) ? 'dd MMM' : 'dd MMM yyyy';

      return <p className="text-sm text-black">{format(transactionDate, dateFormat)}</p>;
    },
    // Excludes column from global filter
    // https://tanstack.com/table/latest/docs/guide/global-filtering#disable-global-filtering
    enableGlobalFilter: false,
    // Number range inclusion
    filterFn: 'inNumberRange',
    size: 10,
    minSize: 10,
  },
  {
    // Do not remove id - it is needed for txn table views where we display a subset of columns
    id: 'vendor',
    accessorFn: (row) => row.vendor?.name,
    header: ({ column }) => <ColumnHeaderWithSorting column={column} label="Vendor" />,
    cell: ({ row }) => {
      // AI vendor enrichment for a transaction is completed the transaction is linked to a vendor
      const vendorEnrichmentCompleted = !!row.original.vendorId;

      const vendorName = vendorEnrichmentCompleted ? (row.original.vendor?.name ?? '') : 'Fetching vendor details...';
      const avatarConfig = vendorEnrichmentCompleted
        ? { showAvatar: true, avatarUrl: row.original.vendor?.logo, size: 'sm' as const }
        : { showAvatar: false };

      const isTransferOrDirectDebit =
        row.original.type === TransactionType.TRANSFER || row.original.type === TransactionType.DIRECT_DEBIT;

      const reference = isTransferOrDirectDebit ? row.original.reference : null;

      return (
        <AvatarNameDescription
          avatar={avatarConfig}
          name={vendorName}
          description={reference}
          // Arbitrary width to make sure the column doesn't take too much space
          // And to avoid there are no jumps in UI when navigating through pages
          // TODO: Tanstack table has a size property, but it doesn't work as expected. Need to investigate further
          // https://github.com/TanStack/table/issues/5115
          className="w-72"
        />
      );
    },
    filterFn: 'arrIncludesSome',
    size: 30,
  },
  {
    accessorKey: 'category',
    header: ({ column }) => <ColumnHeaderWithSorting column={column} label="Category" />,
    accessorFn: ({ vendor }) => vendor?.category?.level2,
    cell: ({ row }) => {
      // AI category enrichment for a transaction is completed the transaction vendor is linked to a category
      const categoryEnrichmentCompleted = !!row.original.vendor?.categoryId;

      const categoryDisplayName = categoryEnrichmentCompleted
        ? (row.original.vendor?.category?.level2 ?? '')
        : 'Fetching category details...';

      return (
        <AvatarNameDescription
          avatar={
            row.original.vendor?.category?.level2
              ? {
                  showAvatar: true,
                  avatarUrl: row.original.vendor?.category?.icon,
                  size: 'xs',
                  faIcon: {
                    isFaIcon: true,
                    faIconName: row.original.vendor?.category?.icon || '',
                  },
                }
              : // If no category, don't show avatar to avoid empty space
                { showAvatar: false }
          }
          name={categoryDisplayName}
        />
      );
    },
    filterFn: 'arrIncludesSome',
    size: 25,
  },
  {
    id: 'account',
    header: ({ column }) => <ColumnHeaderWithSorting column={column} label="Account" />,
    cell: ({ row }) => {
      const { name, logo } = {
        name: row.original.account.institution?.name,
        logo: row.original.account.institution?.logo,
      };

      const accountNameWithCurrency = `${name} (${row.original.account.currency})`;

      return (
        <AvatarNameDescription
          avatar={{ showAvatar: true, avatarUrl: logo, size: 'xs' }}
          name={accountNameWithCurrency ?? ''}
        />
      );
    },

    enableGlobalFilter: false,
    size: 25,
  },
  {
    id: 'status',
    accessorKey: 'status',
    // No header for the status column according to the designs
    header: () => null,
    cell: ({ row }) => {
      const icon = getTransactionStatusIcon(row.original.status);

      return <Inline justify="center">{icon}</Inline>;
    },
    enableGlobalFilter: false,
    size: 10,
    minSize: 10,
  },
  {
    id: 'amount',
    accessorKey: 'amount',
    header: ({ column }) => <ColumnHeaderWithSorting column={column} label="Amount" />,
    accessorFn: (row) => {
      return formatCurrency(Math.abs(row.amount));
    },
    cell: ({ row }) => {
      const normalizedAmount = getNormalizedAmount({ amount: row.original.amount, isCredit: row.original.isCredit });
      const normalizedBaseCurrencyAmount = getNormalizedAmount({
        amount: row.original.baseCurrencyAmount,
        isCredit: row.original.isCredit,
      });
      const shouldDisplayBaseCurrencyAmount = row.original.currency !== row.original.baseCurrency;

      return (
        // Arbitrary width to avoid UI jumps when paginating
        // TODO: Tanstack table has a size property, but it doesn't work as expected. Need to investigate further
        // https://github.com/TanStack/table/issues/5115
        <Stack align="end" space={2}>
          <AmountDisplay normalizedAmount={normalizedAmount} currency={row.original.currency} />

          {shouldDisplayBaseCurrencyAmount && (
            <AmountDisplay
              normalizedAmount={normalizedBaseCurrencyAmount}
              currency={row.original.baseCurrency}
              isSecondary
            />
          )}
        </Stack>
      );
    },
    size: 20,
  },
  {
    id: 'chevron',
    accessorKey: 'chevron',
    // No header for the chevron column
    header: () => null,
    cell: () => <ChevronRight size={ICON_SIZE} color={ICON_COLOR} />,
    enableGlobalFilter: false,
    size: 5,
    minSize: 5,
  },
];

const getTransactionStatusIcon = (status: TransactionStatus) => {
  const baseProps = {
    size: ICON_SIZE,
    color: ICON_COLOR,
  };

  switch (status) {
    case TransactionStatus.PENDING:
      return <Clock3 {...baseProps} />;
    case TransactionStatus.BOOKED:
      // No icon for booked transactions
      return null;
    case TransactionStatus.FAILED:
      return <XCircle {...baseProps} />;
    default:
      return exhaustiveCheck(status);
  }
};
